import React, { useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { Copyright } from '../../components/general/copyright/copyright';
import { typesRedux } from '../../../shared/types/typesRedux';
import { useAuthContext } from '../../../contexts/authContext';
import FieldText from '../../components/general/fields/fieldText';
import { ToastSwal } from '../../components/general/toastSwals/toastSwal';
import { asyncAuthLogin } from '../../../services/auth.service';
import { AuthMessages } from '../../../shared/messages/authMessages';
import CssBaseline from '@mui/material/CssBaseline';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import LoadingButtonGeneral from '../../components/general/buttons/loadingButtonGeneral';
import { FormControlLabel, Switch } from '@mui/material';
import { HOME, UPDATE_PASSWORD } from '../../../routes/paths';
import { Navigate, useNavigate } from 'react-router-dom';

export default function SignIn() {
    const dispatch: any = useDispatch();
    const { logout, modeTheme, tokenAuth } = useAuthContext();
    const { login }: any = AuthMessages;
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const getReCaptcha = async () => {
        if (executeRecaptcha) {
            return await executeRecaptcha('someAction');
        }
    };

    const formik = useFormik({
        initialValues: {
            username: '',
            password: '',
        },
        validationSchema: Yup.object({
            username: Yup.string()
                .required('El usuario es obligatorio')
                .email('El formato del correo es incorrecto'),
            password: Yup.string()
                .required('La calve es obligatoria')
                .min(8, 'La contraseña debe tener al menos 8 caracteres'),
        }),
        validateOnBlur: true,
        onSubmit: async (formSignIn: any) => {
            setLoading(true);
            const captcha = (await getReCaptcha()) || '';

            asyncAuthLogin({ ...formSignIn, captcha })
                .then((body: any) => {
                    if (body.message !== undefined) {
                        if (body.message === 'AUTH-PASSWORD-EXPIRED') {
                            navigate(UPDATE_PASSWORD);
                        }
                        ToastSwal.fire({
                            text: login[body.message],
                            icon: 'error',
                            position:
                                body.message === 'AUTH-PASSWORD-EXPIRED'
                                    ? 'center-end'
                                    : 'top-end',
                        });
                    } else {
                        dispatch({
                            type: typesRedux.AUTH_LOGIN,
                            payload: body,
                        });
                    }
                })
                .finally(() => setLoading(false));
        },
    });

    useEffect(() => {
        dispatch({
            type: typesRedux.LOGOUT_LOGIN,
            payload: '',
        });
        logout();
    }, []);

    const modeThemeClick = () => {
        dispatch({
            type: typesRedux.MODE_THEME,
            payload: modeTheme === 'darkMode' ? 'lightMode' : 'darkMode',
        });
    };

    if (tokenAuth) {
        return <Navigate to={HOME} />;
    }

    return (
        <>
            <Grid
                container
                component="main"
                sx={{ height: '100vh', minWidth: '350px' }}
            >
                <CssBaseline />
                <Grid
                    className="sign_in_grid_image"
                    item
                    xs={false}
                    sm={false}
                    md={8}
                    lg={8.5}
                    xl={9}
                />
                <Grid item xs={12} sm={12} md={4} lg={3.5} xl={3}>
                    <Grid item xs={12} sx={{ textAlign: 'right', mr: 2 }}>
                        <FormControlLabel
                            onClick={modeThemeClick}
                            value={modeTheme || ''}
                            checked={modeTheme === 'darkMode'}
                            control={<Switch color="default" />}
                            label={
                                modeTheme === 'darkMode'
                                    ? 'Modo Claro'
                                    : 'Modo Oscuro'
                            }
                            labelPlacement="start"
                            slotProps={{
                                typography: {
                                    color: 'primary.main',
                                },
                            }}
                        />
                    </Grid>
                    <Box
                        sx={{
                            my: 8,
                            mx: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        {modeTheme === 'darkMode' ? (
                            <img
                                src={require('../../../assets/images/logos/previsora_black.png')}
                                alt="logo"
                            />
                        ) : (
                            <img
                                src={require('../../../assets/images/logos/previsora.png')}
                                alt="logo"
                            />
                        )}
                        <Typography
                            component="h1"
                            variant="h5"
                            sx={{
                                mt: 2,
                                color: 'primary.main',
                            }}
                        >
                            Iniciar Sesión
                        </Typography>
                        <Box component="form" noValidate sx={{ mt: 1 }}>
                            <Grid container spacing={2} mt={1}>
                                <Grid item xs={12}>
                                    <FieldText
                                        label="Correo"
                                        name="username"
                                        required={true}
                                        autoComplete="email"
                                        touched={formik.touched.username}
                                        errors={formik.errors.username}
                                        value={formik.values.username}
                                        onChange={formik.handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FieldText
                                        label="Contraseña"
                                        name="password"
                                        required={true}
                                        autoComplete="current-password"
                                        type="password"
                                        touched={formik.touched.password}
                                        errors={formik.errors.password}
                                        value={formik.values.password}
                                        onChange={formik.handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ textAlign: 'center' }}>
                                    <LoadingButtonGeneral
                                        endIcon={
                                            <LoginRoundedIcon
                                                sx={{
                                                    color: 'primary.contrastText',
                                                }}
                                            />
                                        }
                                        onClick={formik.handleSubmit}
                                        title="Ingresar"
                                        fullWidth={true}
                                        loading={loading}
                                        loadingPosition="end"
                                        sx={{ backgroundColor: 'primary.main' }}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ textAlign: 'center' }}>
                                    <Link
                                        href={UPDATE_PASSWORD}
                                        underline="hover"
                                    >
                                        Actualizar contraseña
                                    </Link>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                    <Copyright className="copyrigth_sign_in" />
                </Grid>
            </Grid>
        </>
    );
}
